import is from '@sindresorhus/is';

import type { Translate } from '~/shared/internationalization/translation';

import type { Thread } from '../data-contract/chat';

export type ThreadStatus = 'idle' | 'needs-input' | 'on-hold' | 'running';

export const THREAD_STATUS_REFETCH_INTERVAL_MS = 5_000;

export type ThreadStatusOptions = {
    readonly hasLiveStream?: boolean;
};

export const isThreadRunningOnBackend = (thread: Thread): boolean => {
    if (thread.status === 'running') return true;
    if (!is.nullOrUndefined(thread.status)) return false;
    return !is.nullOrUndefined(thread.activeRun);
};

const resolveThreadStatus = (
    thread: Thread,
    isWaitingOnAnswer: boolean,
    options: ThreadStatusOptions | undefined,
): ThreadStatus => {
    if (isWaitingOnAnswer) return 'needs-input';
    if (options?.hasLiveStream === true) return 'running';
    if (thread.status === 'running') return 'running';
    if (thread.status === 'resumable') return 'on-hold';
    if (thread.status === 'idle') return 'idle';
    if (isThreadRunningOnBackend(thread)) return 'running';
    if (!is.nullOrUndefined(thread.pendingTurn)) return 'on-hold';
    return 'idle';
};

/** The thread's status for the legacy chat, which waits on an answer while the thread awaits a callback. */
export const getThreadStatus = (thread: Thread, options?: ThreadStatusOptions): ThreadStatus =>
    resolveThreadStatus(thread, !is.nullOrUndefined(thread.pendingTurn?.awaitingCallback), options);

// An expired question can no longer be answered; the thread waits on a new message instead.
const isInterruptAnswerable = (thread: Thread): boolean => {
    const openInterrupt = thread.pendingTurn?.openInterrupt;
    return !is.nullOrUndefined(openInterrupt) && Date.parse(openInterrupt.expiresAt) > Date.now();
};

/** The thread's status for the AG-UI chat, which waits on an answer while the open interrupt is answerable. */
export const getAgUiThreadStatus = (thread: Thread, options?: ThreadStatusOptions): ThreadStatus =>
    resolveThreadStatus(thread, isInterruptAnswerable(thread), options);

export const hasThreadAttentionStatus = (thread: Thread, options?: ThreadStatusOptions): boolean =>
    getThreadStatus(thread, options) !== 'idle';

export const isThreadOnHold = (thread: Thread, options?: ThreadStatusOptions): boolean =>
    getThreadStatus(thread, options) === 'on-hold';

export const threadStatusLabel = (status: ThreadStatus, translate: Translate): string | undefined => {
    switch (status) {
        case 'idle':
            return undefined;
        case 'needs-input':
            return translate('chat.thread-status.needs-input');
        case 'running':
            return translate('chat.thread-status.running');
        case 'on-hold':
            return translate('chat.thread-status.on-hold');
    }
};
